import React, { Suspense, useRef, useState, useImperativeHandle, forwardRef, memo, useCallback } from 'react';
import { Canvas, useFrame, ThreeEvent, useThree } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Grid, Text, Html } from '@react-three/drei';
import { PlacedPallet, TruckSpecs } from '../types';
import * as THREE from 'three';
import { translations as t } from '../utils/translations';
import { Box, LayoutTemplate, Square, ArrowUpFromLine, RotateCw, Trash2, Lock } from 'lucide-react';
import { calculateSnapPosition, getEffectiveDimensions } from '../utils/snapLogic';

interface TruckVisualizerProps {
  loadData: PlacedPallet[];
  truckSpecs: TruckSpecs;
  onPalletMove: (id: string, newPos: { x: number, y: number, z: number, stackedOn: string | null }) => void;
  onRotatePallet: (id: string) => void;
  onDeletePallet: (id: string) => void;
}

export interface TruckVisualizerHandle {
  setCameraView: (view: 'top' | 'side' | 'rear' | 'iso') => void;
  takeScreenshot: () => string;
}

const TruckFrame: React.FC<{ specs: TruckSpecs }> = ({ specs }) => {
  const { width, length, height } = specs;
  
  return (
    <group>
      {/* Floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.05, 0]} receiveShadow>
        <planeGeometry args={[width, length]} />
        <meshStandardMaterial color="#334155" />
      </mesh>
      
      {/* Visual boundaries */}
      <mesh position={[width/2, height/2, 0]}>
         <boxGeometry args={[0.05, height, length]} />
         <meshStandardMaterial color="#94a3b8" opacity={0.3} transparent />
      </mesh>
      <mesh position={[-width/2, height/2, 0]}>
         <boxGeometry args={[0.05, height, length]} />
         <meshStandardMaterial color="#94a3b8" opacity={0.3} transparent />
      </mesh>
       <mesh position={[0, height/2, -length/2]}>
         <boxGeometry args={[width, height, 0.05]} />
         <meshStandardMaterial color="#64748b" /> 
      </mesh>
      
      <Grid 
        position={[0, -0.04, 0]} 
        args={[width, length]} 
        cellSize={0.5} 
        cellThickness={0.5} 
        cellColor="#64748b" 
        sectionSize={1} 
        sectionThickness={1} 
        sectionColor="#94a3b8" 
        fadeDistance={30} 
      />
    </group>
  );
};

// Helper to find pallet below
const findPalletBelow = (
    currentId: string,
    x: number, 
    z: number, 
    myWidth: number,
    myLength: number,
    myWeight: number,
    allPallets: PlacedPallet[]
  ): PlacedPallet | null => {
    
    // Filter potential candidates
    const candidates = allPallets.filter(p => 
        p.id !== currentId && 
        p.stackable
    );
  
    for (const other of candidates) {
      // Calculate effective dimensions for the other pallet (in case it is rotated)
      const otherDims = getEffectiveDimensions(other.width, other.length, other.rotation);
      
      // Check Intersection (AABB with slight margin)
      const xOverlap = Math.abs(x - other.x) < (myWidth + otherDims.width) / 2 - 0.1;
      const zOverlap = Math.abs(z - other.z) < (myLength + otherDims.length) / 2 - 0.1;
  
      if (xOverlap && zOverlap) {
        // Check Weight Capacity
        const currentStackWeight = allPallets
          .filter(p => p.stackedOn === other.id && p.id !== currentId)
          .reduce((sum, p) => sum + p.weight, 0);
        
        if (currentStackWeight + myWeight <= other.maxStackWeight) {
          return other
        }
      }
    }
    return null;
  };

// Draggable Pallet Component - Memoized for Performance
const DraggablePallet: React.FC<{ 
  data: PlacedPallet; 
  allPallets: PlacedPallet[];
  truckSpecs: TruckSpecs;
  onMove: (id: string, newPos: { x: number, y: number, z: number, stackedOn: string | null }) => void;
  onRotate: (id: string) => void;
  onDelete: (id: string) => void;
  setControlsEnabled: (enabled: boolean) => void;
}> = memo(({ data, allPallets, truckSpecs, onMove, onRotate, onDelete, setControlsEnabled }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [hovered, setHover] = useState(false);
  const [validDropTarget, setValidDropTarget] = useState<boolean>(false);
  
  // Refs
  const meshRef = useRef<THREE.Mesh>(null);
  const dragPlane = useRef(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0));
  const dragOffset = useRef(new THREE.Vector3());
  const intersection = new THREE.Vector3();

  // Calculate effective dimensions for the current pallet based on rotation
  // This is used for collision detection logic during drag
  const currentDims = getEffectiveDimensions(data.width, data.length, data.rotation);

  // Smooth Rotation Animation
  useFrame(() => {
      if (meshRef.current) {
          // Pulse effect when hovered/dragged
          if (hovered || isDragging) {
             const scale = 1 + Math.sin(Date.now() * 0.005) * 0.02;
             meshRef.current.scale.setScalar(scale);
          } else {
             meshRef.current.scale.setScalar(1);
          }

          // Lerp rotation to target (data.rotation)
          // Use linear interpolation for smooth 90deg turns
          const currentY = meshRef.current.rotation.y;
          const targetY = data.rotation;
          
          if (Math.abs(targetY - currentY) > 0.001) {
             meshRef.current.rotation.y += (targetY - currentY) * 0.2;
          } else {
             meshRef.current.rotation.y = targetY;
          }
      }
  });

  const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    // Only left click starts drag
    if (e.nativeEvent.button !== 0) return;

    setIsDragging(true);
    setControlsEnabled(false); 
    
    e.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), -data.y), intersection);
    dragOffset.current.copy(intersection).sub(new THREE.Vector3(data.x, data.y, data.z));
    
    dragPlane.current.constant = -data.y;
    
    (e.target as Element).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: ThreeEvent<PointerEvent>) => {
    if (!isDragging) return;
    e.stopPropagation();
    
    e.ray.intersectPlane(dragPlane.current, intersection);
    
    let rawX = intersection.x - dragOffset.current.x;
    let rawZ = intersection.z - dragOffset.current.z;

    // Apply Snap-to-Grid and Magnet
    const snapped = calculateSnapPosition(rawX, rawZ, data, allPallets, truckSpecs);
    let newX = snapped.x;
    let newZ = snapped.z;

    // Hard Boundaries
    const maxX = (truckSpecs.width / 2) - (currentDims.width / 2);
    const maxZ = (truckSpecs.length / 2) - (currentDims.length / 2);
    newX = Math.max(-maxX, Math.min(maxX, newX));
    newZ = Math.max(-maxZ, Math.min(maxZ, newZ));

    const palletBelow = findPalletBelow(
        data.id, 
        newX, 
        newZ, 
        currentDims.width, 
        currentDims.length, 
        data.weight, 
        allPallets
    );

    let newY = data.height / 2;
    let stackedOnId = null;

    if (palletBelow) {
        const alreadyStackedOnTarget = allPallets.filter(
            p => p.stackedOn === palletBelow.id && p.id !== data.id
        );
        const stackHeight = alreadyStackedOnTarget.reduce((h, p) => h + p.height, 0);

        newY = palletBelow.y + (palletBelow.height / 2) + stackHeight + (data.height / 2) + 0.02;
        stackedOnId = palletBelow.id;
        
        setValidDropTarget(true);
        newX = palletBelow.x; 
        newZ = palletBelow.z;
    } else {
        setValidDropTarget(false);
    }

    onMove(data.id, { x: newX, y: newY, z: newZ, stackedOn: stackedOnId });
  };

  const handlePointerUp = (e: ThreeEvent<PointerEvent>) => {
    setIsDragging(false);
    setControlsEnabled(true);
    setValidDropTarget(false);
    (e.target as Element).releasePointerCapture(e.pointerId);
  };

  const handleDoubleClick = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    onRotate(data.id);
  };

  const handleContextMenu = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    // Prevent default browser context menu in parent usually, but good to prevent bubbling
    // onRotate(data.id); // Option: Rotate on right click directly
  };

  // Highlight color
  const color = isDragging ? '#fbbf24' : (hovered ? '#fbbf24' : data.color);
  const opacity = isDragging ? 0.7 : hovered ? 0.9 : 0.85;

  return (
    <group 
      position={[data.x, data.y, data.z]} 
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerOver={() => setHover(true)}
      onPointerOut={() => setHover(false)}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
    >
      <mesh ref={meshRef} castShadow receiveShadow>
        <boxGeometry args={[data.width, data.height, data.length]} />
        <meshStandardMaterial 
          color={color} 
          roughness={0.5} 
          transparent
          opacity={opacity}
        />
      </mesh>
      
      {/* Edges */}
      <mesh rotation={[0, data.rotation, 0]}>
         <lineSegments>
            <edgesGeometry args={[new THREE.BoxGeometry(data.width, data.height, data.length)]} />
            <lineBasicMaterial color={isDragging ? "white" : "black"} linewidth={2} />
        </lineSegments>
      </mesh>

      {/* Floating Context Menu */}
      {hovered && !isDragging && (
        <Html position={[0, data.height/2 + 0.5, 0]} center distanceFactor={10} zIndexRange={[100, 0]}>
            <div className="flex gap-1 bg-slate-900/90 p-1.5 rounded-lg border border-slate-700 shadow-xl backdrop-blur-sm transform transition-all scale-100 origin-bottom">
                <button 
                    onClick={(e) => { e.stopPropagation(); onRotate(data.id); }}
                    className="p-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded transition"
                    title="Rotate 90° (Double Click)"
                >
                    <RotateCw size={14} />
                </button>
                <button 
                    onClick={(e) => { e.stopPropagation(); onDelete(data.id); }}
                    className="p-1.5 bg-red-600 hover:bg-red-500 text-white rounded transition"
                    title="Delete Item"
                >
                    <Trash2 size={14} />
                </button>
            </div>
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1">
                <div className="w-2 h-2 bg-slate-900/90 rotate-45 border-r border-b border-slate-700"></div>
            </div>
        </Html>
      )}

      {/* Weight Label (Rotates with object) */}
      <group rotation={[0, data.rotation, 0]}>
        {data.width > 0.6 && (
            <Text 
                position={[0, data.height/2 + 0.01, 0]} 
                rotation={[-Math.PI/2, 0, 0]} 
                fontSize={0.2} 
                color="white"
                anchorX="center"
                anchorY="middle"
            >
                {`${Math.round(data.weight)}`}
            </Text>
        )}

        {/* Visual Indicators */}
        {data.stackable && !isDragging && (
            <mesh position={[0, data.height / 2 + 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                <ringGeometry args={[0.15, 0.2, 32]} />
                <meshBasicMaterial color="#22c55e" transparent opacity={0.6} side={THREE.DoubleSide} />
            </mesh>
        )}

        {data.stackedOn && (
            <mesh position={[0, -data.height / 2 - 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                <circleGeometry args={[0.15, 32]} />
                <meshBasicMaterial color="#3b82f6" transparent opacity={0.8} side={THREE.DoubleSide} />
            </mesh>
        )}
      </group>

      {/* Drop Target Indicator (Doesn't rotate, stays on floor/target) */}
      {isDragging && validDropTarget && (
         <mesh position={[0, -data.height / 2 - 0.1, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[0.3, 0.4, 32]} />
            <meshBasicMaterial color="#3b82f6" transparent opacity={0.8} />
         </mesh>
      )}
    </group>
  );
});

// Internal component to handle scene logic (Camera/Screenshots) that requires useThree context
const SceneController = forwardRef<TruckVisualizerHandle, { truckSpecs: TruckSpecs, enabled: boolean }>((props, ref) => {
  const { camera, gl, scene } = useThree();
  const controlsRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    setCameraView: (view: 'top' | 'side' | 'rear' | 'iso') => {
      const { length, width, height } = props.truckSpecs;
      const cam = camera as THREE.PerspectiveCamera;
      
      switch (view) {
        case 'top':
          cam.position.set(0, length + 2, 0);
          break;
        case 'side':
          cam.position.set(length + 2, height / 2, 0);
          break;
        case 'rear':
          cam.position.set(0, height + 1, length + 2);
          break;
        case 'iso':
        default:
          cam.position.set(5, length * 0.5, length * 0.8);
          break;
      }
      cam.lookAt(0, 0, 0);
      if (controlsRef.current) controlsRef.current.update();
    },
    takeScreenshot: () => {
      gl.render(scene, camera);
      return gl.domElement.toDataURL('image/png');
    }
  }));

  return (
    <OrbitControls 
      ref={controlsRef}
      enabled={props.enabled}
      minPolarAngle={0} 
      maxPolarAngle={Math.PI / 2.1} 
      target={[0, 1, 0]}
      makeDefault
    />
  );
});


const TruckVisualizer = forwardRef<TruckVisualizerHandle, TruckVisualizerProps>(({ loadData, truckSpecs, onPalletMove, onRotatePallet, onDeletePallet }, ref) => {
  const [controlsEnabled, setControlsEnabled] = useState(true);
  
  // Internal ref to control the SceneController from the buttons inside this component
  const internalRef = useRef<TruckVisualizerHandle>(null);

  // Forward the functionality to the parent Ref
  useImperativeHandle(ref, () => ({
    setCameraView: (view) => internalRef.current?.setCameraView(view),
    takeScreenshot: () => internalRef.current?.takeScreenshot() || ''
  }));

  const camZ = truckSpecs.length * 0.8;
  const camY = truckSpecs.length * 0.5;

  return (
    <div 
      className="w-full h-[500px] bg-slate-900 rounded-lg overflow-hidden shadow-2xl border border-slate-700 relative select-none focus-visible:ring-2 focus-visible:ring-blue-500 group" 
      role="application"
      aria-label="3D Truck Visualization. Drag pallets to move them. Double click to rotate."
      tabIndex={0}
      onContextMenu={(e) => e.preventDefault()} // Prevent context menu globally on canvas
    >
      <div className="absolute top-4 left-4 z-10 bg-black/60 backdrop-blur-sm px-3 py-1 rounded border border-white/10 pointer-events-none">
        <span className="text-xs font-bold text-blue-300">{truckSpecs.name}</span>
      </div>

      {/* Camera Lock Indicator */}
      {!controlsEnabled && (
        <div className="absolute top-16 left-1/2 transform -translate-x-1/2 z-30 flex items-center gap-2 bg-blue-600/90 text-white px-4 py-2 rounded-full shadow-lg backdrop-blur-sm border border-white/20 pointer-events-none animate-in fade-in zoom-in duration-200">
           <Lock size={16} />
           <span className="text-sm font-bold">Camera Locked</span>
        </div>
      )}

      {/* Floating View Controls */}
      <div className="absolute top-1/2 right-4 transform -translate-y-1/2 z-20 flex flex-col gap-2 opacity-100 transition-opacity">
        <div className="bg-slate-900/90 backdrop-blur-sm border border-slate-700 rounded-lg p-1.5 shadow-xl flex flex-col gap-2">
            <button 
                onClick={() => internalRef.current?.setCameraView('iso')} 
                className="p-2 hover:bg-slate-700 text-slate-300 hover:text-white rounded transition group/btn relative"
                aria-label="Isometric View"
            >
                <Box size={20} />
                <span className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-black text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 whitespace-nowrap pointer-events-none">ISO View</span>
            </button>
            <button 
                onClick={() => internalRef.current?.setCameraView('top')} 
                className="p-2 hover:bg-slate-700 text-slate-300 hover:text-white rounded transition group/btn relative"
                aria-label="Top View"
            >
                <LayoutTemplate size={20} />
                <span className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-black text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 whitespace-nowrap pointer-events-none">Top View</span>
            </button>
            <button 
                onClick={() => internalRef.current?.setCameraView('side')} 
                className="p-2 hover:bg-slate-700 text-slate-300 hover:text-white rounded transition group/btn relative"
                aria-label="Side View"
            >
                <Square size={20} />
                <span className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-black text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 whitespace-nowrap pointer-events-none">Side View</span>
            </button>
            <button 
                onClick={() => internalRef.current?.setCameraView('rear')} 
                className="p-2 hover:bg-slate-700 text-slate-300 hover:text-white rounded transition group/btn relative"
                aria-label="Rear View"
            >
                <ArrowUpFromLine size={20} />
                <span className="absolute right-full mr-2 top-1/2 -translate-y-1/2 bg-black text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 whitespace-nowrap pointer-events-none">Rear View</span>
            </button>
        </div>
      </div>
      
      <Canvas shadows dpr={[1, 2]} gl={{ preserveDrawingBuffer: true }}>
        <PerspectiveCamera makeDefault position={[5, camY, camZ]} fov={50} />
        
        {/* Pass internalRef down to the component inside Context */}
        <SceneController ref={internalRef} truckSpecs={truckSpecs} enabled={controlsEnabled} />
        
        <ambientLight intensity={0.5} />
        <directionalLight position={[10, 20, 5]} intensity={1.2} castShadow shadow-mapSize={[1024, 1024]} />
        <pointLight position={[-10, 10, -5]} intensity={0.5} />

        <Suspense fallback={null}>
          <TruckFrame specs={truckSpecs} />
          {loadData.map((pallet) => (
            <DraggablePallet 
              key={pallet.id} 
              data={pallet}
              allPallets={loadData} 
              truckSpecs={truckSpecs}
              onMove={onPalletMove}
              onRotate={onRotatePallet}
              onDelete={onDeletePallet}
              setControlsEnabled={setControlsEnabled}
            />
          ))}
        </Suspense>
      </Canvas>
      <div className="absolute bottom-4 right-4 bg-black/70 p-2 rounded text-xs text-white pointer-events-none text-right border border-white/10">
        <p className="font-medium text-slate-200">Rotate: Double Click / Right Click</p>
        <p className="font-medium text-slate-200">{t.dragItem}</p>
      </div>
    </div>
  );
});

export default TruckVisualizer;